/**
 * ScanResultRail — SC-010 스캔 인식 결과 (FN-023 · FN-026).
 *
 * design 브랜치 ScanResultPanel 의 실 데이터 이식 (2026-08-24). 화면 **상단 가로
 * 캐러셀**로 한 번에 한 장만 크게 보이고 나머지는 옆으로 민다(scroll-snap).
 * 카드 스캔 모드 전용 — 라벨링 모드에는 뜨지 않는다.
 *
 * ⚠ 카메라 영상 위 패널이라 색은 전부 --camera-* 토큰 (tokens.css '카메라 표면').
 *   Astryx Text/Button 은 테마 표면 전제라 여기서는 소박한 마크업을 쓴다.
 */
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../primitives/Icon";
import CardArt from "../card/CardArt";
import { cardImageUrl } from "../../api/card";
import { formatKrw } from "../../lib/formatPrice";
import { maskPrice } from "../../lib/priceMasking";
import type { ScanSessionItem } from "../../store/scanSessionStore";
import styles from "./ScanResultRail.module.css";

interface Props {
  items: ScanSessionItem[];
  /** 비회원이면 시세를 마스킹한다 (§4 priceMasking) */
  authenticated: boolean;
  /** 카드 탭 → 상세 activity (스캔 위에 얹힌다 — ScanPage viewCardDetail) */
  onSelect: (cardSerialNo: number) => void;
  onRemove: (id: string) => void;
}

export default function ScanResultRail({ items, authenticated, onSelect, onRemove }: Props) {
  const { t } = useTranslation("card");

  /**
   * 새 카드가 잡히면 맨 앞(방금 잡은 장)으로 되돌아온다 — 목록은 최신이 앞인데
   * scroll-snap 이 보고 있던 카드를 붙들어 두면 방금 잡은 카드가 왼쪽 밖에 쌓인다.
   */
  const listRef = useRef<HTMLUListElement | null>(null);
  const newestId = items[0]?.id;
  useEffect(() => {
    listRef.current?.scrollTo({ left: 0, behavior: "smooth" });
  }, [newestId]);

  if (items.length === 0) return null;

  return (
    <aside className={styles.rail} role="region" aria-label={t("scanResult.title")}>
      <ul className={styles.list} ref={listRef}>
        {items.map((it, i) => {
          const amount = it.card.listPrice.amount;
          // 즉시 적재 스텁 (2026-08-27) — 이름·번호는 사이드카, 이미지·시세는 하이드
          // 레이션 대기 중. 시세 자리에 "시세 없음" 대신 스켈레톤을 깜빡여 로딩임을 알린다.
          const pending = it.card.cardUid.startsWith("serl:");
          return (
            <li
              key={it.id}
              className={`${styles.cardItem} ${i === 0 ? styles.cardItemLatest : ""}`}
            >
              {/* 카드 몸통(썸네일+정보)이 상세 진입 버튼이다 — 제거(×)는 밖에 둬야
                  버튼이 겹치지 않는다. 하이드레이션 전 스텁도 serlno 는 실값이라
                  바로 열 수 있다. */}
              <button
                type="button"
                className={styles.openButton}
                onClick={() => onSelect(it.card.cardSerialNo)}
                aria-label={t("scanResult.open", { name: it.card.name })}
              >
                {pending ? (
                  <span className={`${styles.thumb} ${styles.thumbSkeleton}`} aria-hidden />
                ) : (
                  <CardArt
                    className={styles.thumb}
                    imgUrl={cardImageUrl(it.card.imagePathway) ?? undefined}
                  />
                )}
                <span className={styles.info}>
                  <span className={styles.name}>{it.card.name}</span>
                  <span className={styles.meta}>
                    {[it.card.number, it.card.language.toUpperCase()]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                  <span className={styles.price}>
                    {pending ? (
                      <span className={styles.priceSkeleton} aria-hidden />
                    ) : amount === null ? (
                      t("scanResult.noPrice")
                    ) : authenticated ? (
                      formatKrw(amount)
                    ) : (
                      maskPrice(amount)
                    )}
                  </span>
                </span>
              </button>
              <button
                type="button"
                className={styles.removeButton}
                onClick={() => onRemove(it.id)}
                aria-label={t("scanResult.remove", { name: it.card.name })}
              >
                {/* ⚠ '✕' 문자를 직접 찍으면 이 자리만 시스템 글꼴이다 — 화면의
                    나머지 아이콘은 전부 PEEKA 픽셀 세트다 (design). */}
                <Icon
                  name="Close"
                  size="sm"
                  ariaLabel={t("scanResult.remove", { name: it.card.name })}
                />
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
